import dayjs, { Dayjs } from "dayjs"
import { CrewMember, UpdateScheduledEventRequest, DateRangeViewEvent, TaskCalendarEvent } from "../scheduleApi"
import { findCrewMemberByEmail } from "../crewModel"

export const SPANNING_EVENT_ROW_HEIGHT = 24
export const SPANNING_EVENT_GAP = 2
export const MONTH_VIEW_SPANNING_ROW_HEIGHT = 64
export const WEEK_VIEW_SPANNING_ROW_HEIGHT = 60
export const MONTH_VIEW_DAY_HEADER_HEIGHT = 40

type SpanningSegmentBase = {
  weekIndex: number
  startDayIndex: number
  endDayIndex: number
  spanWidth: number
  isStart: boolean
  isEnd: boolean
  rowIndex: number
}

export type SpanningEventSegment = SpanningSegmentBase & {
  event: DateRangeViewEvent
}

export type SpanningTaskSegment = SpanningSegmentBase & {
  task: TaskCalendarEvent
}

export type WeekSpanningLayout = {
  segments: SpanningEventSegment[]
  taskSegments: SpanningTaskSegment[]
  totalRows: number
  rowsPerDay: number[]
}

type SpanningCandidate = { start: string; end: string; allDay: boolean }

const isSpanning = (item: SpanningCandidate): boolean => {
  if (item.allDay) {
    return true
  }
  const startDay = dayjs(item.start).startOf("day")
  const endDay = dayjs(item.end).startOf("day")
  return !startDay.isSame(endDay, "day")
}

export const isSpanningEvent = (event: DateRangeViewEvent): boolean => isSpanning(event)

export const isSpanningTask = (task: TaskCalendarEvent): boolean => isSpanning(task)

export const hasSpanningEventOnDay = (segments: SpanningEventSegment[], dayIndex: number): boolean =>
  segments.some((segment) => segment.startDayIndex <= dayIndex && dayIndex <= segment.endDayIndex)

export const hasSpanningTaskOnDay = (segments: SpanningTaskSegment[], dayIndex: number): boolean =>
  segments.some((segment) => segment.startDayIndex <= dayIndex && dayIndex <= segment.endDayIndex)

type SegmentPosition = {
  weekIndex: number
  startDayIndex: number
  endDayIndex: number
  spanWidth: number
  isStart: boolean
  isEnd: boolean
}

const calculateSegmentPositions = (
  item: { start: string; end: string },
  weekStarts: Dayjs[]
): SegmentPosition[] => {
  const positions: SegmentPosition[] = []
  const itemStart = dayjs(item.start).startOf("day")
  const itemEnd = dayjs(item.end).startOf("day")

  weekStarts.forEach((weekStartRaw, weekIndex) => {
    const weekStart = weekStartRaw.startOf("day")
    const weekEnd = weekStartRaw.endOf("week").startOf("day")

    if (itemEnd.isBefore(weekStart) || itemStart.isAfter(weekEnd)) {
      return
    }

    const segmentStart = itemStart.isBefore(weekStart) ? weekStart : itemStart
    const segmentEnd = itemEnd.isAfter(weekEnd) ? weekEnd : itemEnd

    const startDayIndex = segmentStart.diff(weekStart, "day")
    const endDayIndex = segmentEnd.diff(weekStart, "day")

    if (startDayIndex < 0 || endDayIndex > 6) {
      return
    }

    positions.push({
      weekIndex,
      startDayIndex,
      endDayIndex,
      spanWidth: endDayIndex - startDayIndex + 1,
      isStart: itemStart.isSame(segmentStart, "day"),
      isEnd: itemEnd.isSame(segmentEnd, "day"),
    })
  })

  return positions
}

export const getSpanningEventSegments = (
  event: DateRangeViewEvent,
  weekStarts: Dayjs[]
): Omit<SpanningEventSegment, "rowIndex">[] => {
  return calculateSegmentPositions(event, weekStarts).map(pos => ({ ...pos, event }))
}

export const getSpanningTaskSegments = (
  task: TaskCalendarEvent,
  weekStarts: Dayjs[]
): Omit<SpanningTaskSegment, "rowIndex">[] => {
  return calculateSegmentPositions(task, weekStarts).map(pos => ({ ...pos, task }))
}

type AllocatableSegment = {
  startDayIndex: number
  endDayIndex: number
  spanWidth: number
}

const allocateRows = <T extends AllocatableSegment>(
  segments: T[]
): (T & { rowIndex: number })[] => {
  const sortedSegments = [...segments].sort((a, b) => {
    if (a.spanWidth !== b.spanWidth) {
      return b.spanWidth - a.spanWidth
    }
    return a.startDayIndex - b.startDayIndex
  })

  const rowOccupancy: boolean[][] = []
  const allocatedSegments: (T & { rowIndex: number })[] = []

  for (const segment of sortedSegments) {
    let assignedRow = -1

    for (let rowIndex = 0; rowIndex < rowOccupancy.length; rowIndex++) {
      const row = rowOccupancy[rowIndex]
      let canFit = true

      for (let day = segment.startDayIndex; day <= segment.endDayIndex; day++) {
        if (row[day]) {
          canFit = false
          break
        }
      }

      if (canFit) {
        assignedRow = rowIndex
        break
      }
    }

    if (assignedRow === -1) {
      assignedRow = rowOccupancy.length
      rowOccupancy.push(new Array(7).fill(false))
    }

    for (let day = segment.startDayIndex; day <= segment.endDayIndex; day++) {
      rowOccupancy[assignedRow][day] = true
    }

    allocatedSegments.push({
      ...segment,
      rowIndex: assignedRow,
    })
  }

  return allocatedSegments
}

export const allocateSpanningEventRows = (
  segments: Omit<SpanningEventSegment, "rowIndex">[]
): SpanningEventSegment[] => {
  return allocateRows(segments)
}

export const calculateWeekSpanningLayouts = (
  spanningEvents: DateRangeViewEvent[],
  weekStarts: Dayjs[],
  spanningTasks: TaskCalendarEvent[] = []
): WeekSpanningLayout[] => {
  const allEventSegments: Omit<SpanningEventSegment, "rowIndex">[] = []
  for (const event of spanningEvents) {
    allEventSegments.push(...getSpanningEventSegments(event, weekStarts))
  }

  const allTaskSegments: Omit<SpanningTaskSegment, "rowIndex">[] = []
  for (const task of spanningTasks) {
    allTaskSegments.push(...getSpanningTaskSegments(task, weekStarts))
  }

  const eventSegmentsByWeek = new Map<number, Omit<SpanningEventSegment, "rowIndex">[]>()
  for (const segment of allEventSegments) {
    const weekSegments = eventSegmentsByWeek.get(segment.weekIndex) || []
    weekSegments.push(segment)
    eventSegmentsByWeek.set(segment.weekIndex, weekSegments)
  }

  const taskSegmentsByWeek = new Map<number, Omit<SpanningTaskSegment, "rowIndex">[]>()
  for (const segment of allTaskSegments) {
    const weekSegments = taskSegmentsByWeek.get(segment.weekIndex) || []
    weekSegments.push(segment)
    taskSegmentsByWeek.set(segment.weekIndex, weekSegments)
  }

  return weekStarts.map((_, weekIndex) => {
    const weekEventSegments = eventSegmentsByWeek.get(weekIndex) || []
    const weekTaskSegments = taskSegmentsByWeek.get(weekIndex) || []

    const allSegments: (
      | (Omit<SpanningEventSegment, "rowIndex"> & { _kind: 'event' })
      | (Omit<SpanningTaskSegment, "rowIndex"> & { _kind: 'task' })
    )[] = [
      ...weekEventSegments.map(s => ({ ...s, _kind: 'event' as const })),
      ...weekTaskSegments.map(s => ({ ...s, _kind: 'task' as const })),
    ]

    const allocated = allocateRows(allSegments)

    const segments: SpanningEventSegment[] = []
    const taskSegments: SpanningTaskSegment[] = []

    for (const item of allocated) {
      if (item._kind === 'event') {
        const { _kind: _, ...rest } = item
        segments.push(rest as SpanningEventSegment)
      } else {
        const { _kind: _, ...rest } = item
        taskSegments.push(rest as SpanningTaskSegment)
      }
    }

    const totalRows = allocated.length > 0
      ? Math.max(...allocated.map((s) => s.rowIndex)) + 1
      : 0

    const rowsPerDay = new Array(7).fill(0)
    for (const item of allocated) {
      const requiredRows = item.rowIndex + 1
      for (let day = item.startDayIndex; day <= item.endDayIndex; day++) {
        if (requiredRows > rowsPerDay[day]) {
          rowsPerDay[day] = requiredRows
        }
      }
    }

    return {
      segments,
      taskSegments,
      totalRows,
      rowsPerDay,
    }
  })
}

export const mergePreviewIntoRowsPerDay = (
  rowsPerDay: number[],
  previewSegments: SpanningEventSegment[] | undefined
): number[] => {
  const merged = [...rowsPerDay]
  if (!previewSegments) {
    return merged
  }
  for (const segment of previewSegments) {
    const requiredRows = segment.rowIndex + 1
    for (let day = segment.startDayIndex; day <= segment.endDayIndex; day++) {
      if (requiredRows > merged[day]) {
        merged[day] = requiredRows
      }
    }
  }
  return merged
}

export const calculateReservedHeight = (totalRows: number, rowHeight: number = SPANNING_EVENT_ROW_HEIGHT): number => {
  if (totalRows === 0) {
    return 0
  }
  return totalRows * rowHeight + (totalRows - 1) * SPANNING_EVENT_GAP
}

export type BuildResizeSpanningEventRequestParams = {
  event: DateRangeViewEvent
  newEndDate: Dayjs
  crew: CrewMember[]
}

export const buildResizeSpanningEventRequest = ({
  event,
  newEndDate,
  crew,
}: BuildResizeSpanningEventRequestParams): UpdateScheduledEventRequest | null => {
  const originalStart = dayjs(event.start)
  const originalEnd = dayjs(event.end)

  if (newEndDate.isSame(originalEnd, 'day')) {
    return null
  }

  if (newEndDate.isBefore(originalStart, 'day')) {
    return null
  }

  const newEnd = newEndDate
    .hour(originalEnd.hour())
    .minute(originalEnd.minute())
    .second(originalEnd.second())

  const durationMinutes = newEnd.diff(originalStart, 'minutes')

  const crewLead = event.crewForemanEmail
    ? findCrewMemberByEmail(crew, event.crewForemanEmail)
    : null

  return {
    eventId: event.id,
    start: originalStart.toISOString(),
    end: newEnd.toISOString(),
    dayStartTime: null,
    estimatedDurationInMinutes: durationMinutes,
    crewMemberIds: event.crewMemberIds || '',
    crewLeadId: crewLead?.id ?? null,
    workareaIds: event.workAreaIds || '',
    routeId: event.routeId,
    allDay: event.allDay,
    sendCrewLeadNotifications: event.sendCrewLeadNotifications,
    frozen: event.frozen,
  }
}
